import { useEffect } from 'react';
import type { i18n as I18n } from 'i18next';
import { useCustomQuery } from './apollo/ApolloCustomHooks';
import { Language } from '../interfaces/language';
import { defaultLanguage } from '../i18n/i18n';
import { Query } from '../services/graphql/query';

const emptyLanguages: Language[] = [];

export function useAppLanguages(
  i18n: I18n,
  userLanguageId?: number,
): Language[] {
  const { data, error } = useCustomQuery<{ languages: Language[] }>(
    Query.languages,
  );

  useEffect(() => {
    if (error) console.error('App.languages', error);
  }, [error]);

  useEffect(() => {
    if (!data?.languages) return;
    const languages = data.languages;
    const preferred = languages.find(
      (language) => language.id === userLanguageId,
    )?.language_code;
    const currentAvailable = languages.some(
      (language) => language.language_code === i18n.language,
    );
    const target =
      preferred || (currentAvailable ? i18n.language : defaultLanguage);
    if (target !== i18n.language) {
      const report = (error: unknown) => {
        if (error) console.error('App.languages.changeLanguage', error);
      };
      void i18n.changeLanguage(target, report).catch(report);
    }
    if (!preferred && !currentAvailable) {
      const report = (error: unknown) => {
        if (error) console.error('App.languages.loadLanguages', error);
      };
      void i18n
        .loadLanguages(
          languages.map((language) => language.language_code),
          report,
        )
        .catch(report);
    }
  }, [data?.languages, i18n, userLanguageId]);

  return data?.languages ?? emptyLanguages;
}
